import { DeeplyReadonly, Filter, Query } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { DescriptionOutlined } from '@mui/icons-material';
import { Box, Button, Dialog, DialogContent, Link } from '@mui/material';
import { CuriosityNamespaceType, CuriosityValue } from '@procureai/curiosity-ui-mui';
import { Operator } from '@procureai/query-builder';
import {
  formatNumber,
  LinearProgressChart,
  LinearProgressList,
  LinearProgressListItem,
  LinearProgressListSubheader,
  NumberFormatPreset,
  Widget,
  WidgetContent,
  WidgetFooter,
  WidgetHeader,
  WidgetIcon,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { isEmpty } from 'ramda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { SupplierFilterField } from '@/generated/gql/graphql';
import { BaseDomainType, I18nNamespace } from '~/common/types';
import { DimPurchaseOrderField } from '~/common/types/cubes/DimPuchaseOrder';
import { DimPurchaseOrderItemField } from '~/common/types/cubes/DimPurchaseOrderItem';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { DEFAULT_DATE_RANGE } from '~/components/DateRangeProvider/DateRangeProvider.util';
import { EncodedQuery } from '~/components/EncodedQuery/EncodedQuery';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { VirtualList, VirtualListProps } from '~/components/VirtualList/VirtualList';
import { useOpenClose } from '~/hooks';
import { useVariableCubeQuery } from '~/hooks/useVariableQuery/useVariableQuery';
import { RouteId } from '~/routes';
import { convertDateRangeToTimeDimension } from '~/util/cube';

const MAX_ITEMS = 3;

const segmentMapper = (item: any): { id: string; value: number } => {
  return {
    id: item[DimPurchaseOrderField.SUPPLIER_ID],
    value: item[DimPurchaseOrderField.TOTAL_VALUE_NORMALIZED],
  };
};

export const CommodityGroupSupplierListWidget = () => {
  const { entityId } = useModuleContext();

  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'CommodityGroupSupplierListWidget' });
  const [widgetRef, active] = useScrollIntoView({ hash: 'commodity-group-supplier-list' });

  const [dialogProps, dialogOpen] = useOpenClose();

  const filters: DeeplyReadonly<Filter[]> = [
    {
      member: DimPurchaseOrderItemField.COMMODITY_GROUP_ID,
      operator: 'equals',
      values: [entityId],
    },
    {
      member: DimPurchaseOrderField.TOTAL_VALUE_NORMALIZED,
      operator: 'gt',
      values: ['0'],
    },
  ];
  const timeDimensions = convertDateRangeToTimeDimension(DimPurchaseOrderField.CREATED_AT, DEFAULT_DATE_RANGE);

  const totalOrderVolumeQuery = useCubeQuery({
    measures: [DimPurchaseOrderField.TOTAL_VALUE_NORMALIZED],
    dimensions: [DimPurchaseOrderItemField.COMMODITY_GROUP_ID],
    filters,
    timeDimensions,
  });

  const totalOrderVolume =
    (totalOrderVolumeQuery.resultSet?.rawData()[0]?.[DimPurchaseOrderField.TOTAL_VALUE_NORMALIZED] as number) ?? null;

  const supplierQuery: DeeplyReadonly<Query> = {
    order: {
      [DimPurchaseOrderField.TOTAL_VALUE_NORMALIZED]: 'desc',
    },
    dimensions: [DimPurchaseOrderField.SUPPLIER_ID],
    measures: [DimPurchaseOrderField.TOTAL_VALUE_NORMALIZED],
    filters,
    timeDimensions,
  };

  const { rows, loading } = useVariableCubeQuery({ ...supplierQuery, limit: MAX_ITEMS }, { segmentMapper });
  const hasNodes = !isEmpty(rows);

  const rules = useMemo(
    () => [{ field: SupplierFilterField.commodityGroupId, value: [entityId], operator: Operator.IN }],
    [entityId],
  );

  const renderItem: NonNullable<VirtualListProps<any>['renderItem']> = useMemo(
    () =>
      ({ item }) => {
        const { id, value } = item;

        return (
          <Link key={id} component={RouterLink} to={RouteId.supplierDetail(id)} underline="none">
            <Box
              style={{ listStyleType: 'none' }}
              sx={{
                py: 4,
              }}
            >
              <LinearProgressListItem
                key={id}
                component="div"
                primary={<CuriosityValue ns={CuriosityNamespaceType.SUPPLIER} i18nKey={id} />}
                primaryRight={t(!totalOrderVolume ? 'value' : 'valueTotal', {
                  spent: value,
                  total: totalOrderVolume,
                })}
              >
                {value !== null && totalOrderVolume !== null && (
                  <LinearProgressChart
                    value={value}
                    max={totalOrderVolume}
                    tooltipFormatter={formatNumber(NumberFormatPreset.CURRENCY)}
                  />
                )}
              </LinearProgressListItem>
            </Box>
          </Link>
        );
      },

    [t, totalOrderVolume],
  );

  return (
    <>
      <Widget ref={widgetRef} active={active} data-intercom-target="CommoditySupplierListWidget">
        <WidgetHeader title={t('title')} icon={<WidgetIcon icon={<DescriptionOutlined fontSize="small" />} />} />
        <WidgetContent>
          {hasNodes ? (
            <LinearProgressList
              items={rows}
              renderItem={renderItem}
              subheader={
                <LinearProgressListSubheader disableGutters disableSticky>
                  {t('listSubheaderVolume')}
                </LinearProgressListSubheader>
              }
            />
          ) : loading ? (
            <CircularProgressCentered sx={{ position: 'relative' }} />
          ) : (
            <PlaceholderWidget />
          )}
        </WidgetContent>
        <WidgetFooter>
          <Button variant="text" disabled={isEmpty(rows)} onClick={dialogOpen}>
            {t('showAll')}
          </Button>
          <EncodedQuery pathname={RouteId.analyticsBase(BaseDomainType.SUPPLIER)} rules={rules}>
            {(linkProps) => (
              <Button variant="text" disabled={isEmpty(rows)} {...linkProps}>
                {t('showInBase')}
              </Button>
            )}
          </EncodedQuery>
        </WidgetFooter>
      </Widget>
      <Dialog
        {...dialogProps}
        sx={{
          '& .MuiDialog-paper': {
            background: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent>
          <Widget>
            <WidgetHeader
              title={t('modal.title')}
              icon={<WidgetIcon icon={<DescriptionOutlined fontSize="small" />} />}
            />
            <WidgetContent>
              <Box
                sx={{
                  marginBottom: 16,
                }}
              >
                <VirtualList
                  itemSize={76}
                  query={supplierQuery}
                  queryOptions={{
                    skip: !dialogProps.open,
                    variables: {
                      id: entityId,
                    },
                    segmentMapper,
                  }}
                  renderItem={renderItem}
                />
              </Box>
            </WidgetContent>
          </Widget>
        </DialogContent>
      </Dialog>
    </>
  );
};
